/**
 * Members Store — manages board member list and profiles.
 * Domain: Board Members
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'

export const useMembersStore = defineStore('members', () => {
  // ─── State ───────────────────────────────────────────────────────────────
  const items        = ref([])
  const loading      = ref(false)
  const searchQuery  = ref('')
  const statusFilter = ref('')
  const roleFilter   = ref('')

  // ─── Getters ─────────────────────────────────────────────────────────────
  const filtered = computed(() => {
    let list = items.value
    if (searchQuery.value) {
      const q = searchQuery.value.toLowerCase()
      list = list.filter(m =>
        m.first_name?.toLowerCase().includes(q) ||
        m.last_name?.toLowerCase().includes(q) ||
        m.email?.toLowerCase().includes(q)
      )
    }
    if (statusFilter.value) list = list.filter(m => m.status === statusFilter.value)
    if (roleFilter.value)   list = list.filter(m => m.role   === roleFilter.value)
    return list
  })

  const active = computed(() => items.value.filter(m => m.status === 'active'))

  // ─── Actions ─────────────────────────────────────────────────────────────
  const fetchAll = async (params = {}) => {
    loading.value = true
    try {
      const response = await api.get('/members', { params })
      items.value = response.data.members || []
      return items.value
    } catch (error) {
      console.error('Failed to fetch members:', error)
      return []
    } finally {
      loading.value = false
    }
  }

  const fetchById = async (id) => {
    try {
      const response = await api.get(`/members/${id}`)
      return response.data
    } catch (error) {
      console.error(`Failed to fetch member ${id}:`, error)
      throw error
    }
  }

  const create = async (data) => {
    try {
      const response = await api.post('/members', data)
      const newItem = response.data.member
      items.value.push(newItem)
      return { success: true, member: newItem }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to create member' }
    }
  }

  const update = async (id, data) => {
    try {
      const response = await api.put(`/members/${id}`, data)
      const updated = response.data.member
      const idx = items.value.findIndex(m => m.id === id)
      if (idx !== -1) items.value[idx] = updated
      return { success: true, member: updated }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to update member' }
    }
  }

  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active'
    try {
      const response = await api.patch(`/members/${id}/status`, { status: newStatus })
      const updated = response.data.member
      const idx = items.value.findIndex(m => m.id === id)
      if (idx !== -1) items.value[idx] = updated
      return { success: true, member: updated }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to toggle member status' }
    }
  }

  const updateCommittees = async (memberId, committeeIds) => {
    try {
      await api.put(`/members/${memberId}/committees`, { committee_ids: committeeIds })
      return { success: true }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to update committees' }
    }
  }

  const resetPassword = async (memberId, newPassword) => {
    try {
      await api.post(`/members/${memberId}/reset-password`, { new_password: newPassword })
      return { success: true }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to reset password' }
    }
  }

  return {
    // State
    items, loading, searchQuery, statusFilter, roleFilter,
    // Getters
    filtered, active,
    // Actions
    fetchAll, fetchById, create, update, toggleStatus, updateCommittees, resetPassword
  }
})
